import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type {
  FilterPreset,
  Filters,
  SnapshotId,
  TableColumnSort,
  TableEndpoint,
  TableSettings,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useApiTableMenuItems } from 'components/ApiTable/components/useApiTableMenuItems';
import { loadApiTableRowIdsForSelectAll } from 'components/ApiTable/helpers/loadApiTableRowIdsForSelectAll';
import { applyExternalFilters } from 'components/ApiTable/model/commands/applyExternalFilters';
import {
  type ChangeFilterCommand,
  changeFilter,
} from 'components/ApiTable/model/commands/changeFilter';
import {
  type ChangeIntentFilterCommand,
  changeIntentFilter,
} from 'components/ApiTable/model/commands/changeIntentFilter';
import { changeSort } from 'components/ApiTable/model/commands/changeSort';
import { handleRequestTableRowRange } from 'components/ApiTable/model/commands/handleRequestTableRowRange';
import { loadTableRows } from 'components/ApiTable/model/commands/loadTableRows';
import { reloadTableData } from 'components/ApiTable/model/commands/reloadTableData';
import { restoreSettings } from 'components/ApiTable/model/commands/restoreSettings';
import { selectFilterPresetById } from 'components/ApiTable/model/commands/selectFilterPresetById';
import { submitSimpleFilters } from 'components/ApiTable/model/commands/submitSimpleFilters';
import { selectPatchedData } from 'components/ApiTable/model/selectors/selectPatchedData';
import { selectTableId } from 'components/ApiTable/model/selectors/selectTableId';
import { useReloadOnAddedColumns } from 'components/ApiTable/model/useReloadOnAddedColumns';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { useLazyLoadRows } from 'components/LazyRowContainer/useLazyLoadRows';
import { useRuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import type { TableCellContext } from 'components/Table/model/TableCellContext';
import type {
  LoadRowIdsForSelectAllFunction,
  TableBulkAction,
  TableExternalFilter,
  TableRowIdValue,
} from 'components/Table/model/TableModel';
import { useTable } from 'components/Table/model/useTable';
import { useLocale } from 'containers/App/LocaleContext';
import { record } from 'fp-ts';
import { type ReactNode, useEffect, useMemo } from 'react';
import deepEqual from 'react-fast-compare';
import {
  type CommandModel,
  useCommandModel,
  workaroundExtractModelCurrentState,
} from 'utils/hooks/useCommandModel';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import { useEffectOnChange } from 'utils/react/useEffectOnChange';
import { useApi } from 'utils/request/useApi';
import {
  type ApiTableAdditionalCommandContext,
  type ApiTableModel,
  type ApiTableOuterState,
  type ApiTableOuterStateProps,
  type ApiTableState,
  type SubmitFiltersEvent,
  createApiTableInitialState,
} from './ApiTableModel';
import { initializeSimpleFilters } from './commands/initializeSimpleFilters';
import { loadTableReports } from './commands/loadTableReports';
import { setFilters } from './commands/setFilters';

export interface UseApiTableProps extends ApiTableOuterStateProps {
  bulkActions?: TableBulkAction[];
  description?: ReactNode;
  enableTableReports?: boolean;
  disableViewUrl?: boolean;
  endpoint: TableEndpoint<any>;
  externalSettings?: TableSettings;
  snapshotId?: SnapshotId | null;
  initialFilterPreset?: FilterPreset;
  initialFilters?: Filters;
  initialSelectedRows?: TableRowIdValue[];
  initialSort?: TableColumnSort;
  noDataBox?: ReactNode;
  onFiltersSubmit?: SubmitFiltersEvent;
  showOnlyIntentResultsOnColumns?: string[];
  staticFilters?: Filters;
  tableCellContext?: Partial<TableCellContext>;
  temporarySettings?: boolean;
  titleEndAdornment?: ReactNode;
}

export const useApiTable = (props: UseApiTableProps): ApiTableModel => {
  const currentSnapshotId = useSnapshotId();
  const snapshotId = props.snapshotId ?? currentSnapshotId;

  const globalCtxProps = useGlobalStateContextSelectors({
    attributeFilters: prop('attributeFilters'),
    timezone: prop('user', 'timezone'),
  });

  const ruleContext = useRuleContext();
  const api = useApi();

  const model: CommandModel<
    ApiTableState,
    ApiTableAdditionalCommandContext,
    ApiTableOuterState
  > = useCommandModel(
    () =>
      createApiTableInitialState(
        props.initialFilters,
        props.initialFilterPreset,
        props.initialSort,
      ),
    {
      api,
      getTableModel: useStableCallback(() => tableModel),
      getLazyLoadRows: useStableCallback(() => lazyLoadRows),
      msg: useLocale(),
      notifications: useNotifications(),
      onFiltersSubmit: props.onFiltersSubmit,
    },
    {
      bindVariables: props.bindVariables,
      dataPatch: props.dataPatch,
      enableIntentEditor: props.enableIntentEditor,
      enableTableReports: props.enableTableReports || props.enableIntentEditor,
      disableTableRowDetail: props.disableTableRowDetail,
      endpoint: props.endpoint,
      id: props.id,
      initialFilters: props.initialFilters,
      loading: props.loading,
      patchRow: props.patchRow,
      renderCustomApiDoc: props.renderCustomApiDoc,
      rowIdField: props.rowIdField,
      rowSelectionFilter: props.rowSelectionFilter,
      snapshotId: props.snapshotId ?? snapshotId,
      staticFilters: props.staticFilters,
      tableDescription: props.tableDescription,
      tableRowDetailContainer: props.tableRowDetailContainer,
      attributeFilters: globalCtxProps.attributeFilters,
      ruleContext,
      timezone: globalCtxProps.timezone,
    },
  );

  const menuItems = useApiTableMenuItems({
    disableViewUrl: props.disableViewUrl,
    dispatch: model.dispatch,
    state: model,
    menuItems: props.menuItems,
  });

  const handleChangeFilter = useStableCallback(
    (command: ChangeFilterCommand) => {
      model.dispatch(changeFilter, command);
    },
  );

  const handleChangeIntentFilter = useStableCallback(
    (command: ChangeIntentFilterCommand) => {
      model.dispatch(changeIntentFilter, command);
    },
  );

  const handleRequestRowRange = useStableCallback(
    (startIndex: number, endIndex: number) => {
      model.dispatch(handleRequestTableRowRange, {
        rowRange: { startIndex, endIndex },
      });
    },
  );

  const handleRestoreSettings = useStableCallback(() => {
    model.dispatch(restoreSettings);
  });

  const handleSelectFilterPreset = useStableCallback(
    (presetId: string | null) => {
      model.dispatch(selectFilterPresetById, { presetId });
    },
  );

  const handleSortChange = useStableCallback((sort: TableColumnSort) => {
    model.dispatch(changeSort, { sort });
  });

  const handleSubmitFilters = useStableCallback(() => {
    model.dispatch(submitSimpleFilters);
  });

  const handleRemoveFilters = useStableCallback(() => {
    // We intentionally ignore the argument of `onRemoveFilters` there:
    // the only external filter provided by ApiTable is `advanced` filter,
    // and when that is active, none of other filters can be active - therefore
    // we remove all filters in all cases.
    model.dispatch(initializeSimpleFilters, { initialFilters: {} });
    model.dispatch(setFilters, {
      filters: {},
      origin: 'empty',
    });
    model.dispatch(reloadTableData);
  });

  const loadRowIdsForSelectAll: LoadRowIdsForSelectAllFunction =
    useStableCallback(
      (
        omitWhenFilterIsNotPresent?: boolean,
      ): Promise<TableRowIdValue[] | null> =>
        omitWhenFilterIsNotPresent &&
        record.size(model.lastRequestPayload?.filters ?? {}) === 0
          ? Promise.resolve(null)
          : loadApiTableRowIdsForSelectAll(api, model),
    );

  const tableCellContext = useMemo(
    () => ({
      timezone: globalCtxProps.timezone,
      ...(props.tableCellContext ? props.tableCellContext : {}),
    }),
    [globalCtxProps.timezone, props.tableCellContext],
  );

  const externalFilters: TableExternalFilter[] = useMemo(() => {
    switch (model.filtersOrigin) {
      case 'advanced':
        return [{ key: 'advanced', label: 'Advanced filter' }];
      case 'preset':
        return [
          {
            key: 'preset',
            label: `Preset: ${model.selectedFilterPreset?.name ?? '(no name)'}`,
          },
        ];
      default:
        return [];
    }
  }, [model.filtersOrigin, model.selectedFilterPreset?.name]);

  const tableModel = useTable({
    id: selectTableId(model),
    bulkActions: props.bulkActions,
    canRowBeSelected: model.rowSelectionFilter?.canRowBeSelected,
    disableSelectAll: model.rowSelectionFilter?.disableSelectAll,
    columns: model.endpoint.columns,
    data: selectPatchedData(model),
    description: props.description,
    externalSettings: props.temporarySettings ? {} : props.externalSettings,
    externalFilters,
    onRemoveFilters: handleRemoveFilters,
    initialSelectedRows: props.initialSelectedRows,
    loading: props.loading,
    loadRowIdsForSelectAll,
    menuItems,
    noDataBox: props.noDataBox,
    onChangeFilter: handleChangeFilter,
    onChangeIntentFilter: handleChangeIntentFilter,
    onRequestRowRange: handleRequestRowRange,
    onRestoreSettings: handleRestoreSettings,
    onSubmitFilters: handleSubmitFilters,
    reports: model.reports,
    rowCount: model.rowCount,
    rowCountMore: model.rowCountMore,
    rowIdField: model.rowIdField,
    selectedFilterPreset: model.selectedFilterPreset,
    onSelectFilterPreset: handleSelectFilterPreset,
    showOnlyIntentResultsOnColumns: props.showOnlyIntentResultsOnColumns,
    simpleFilterFields: model.simpleFilterFields,
    simpleFilterFieldsSubmitted:
      model.filtersOrigin === 'simple'
        ? model.simpleFilterFieldsSubmitted
        : undefined,
    sort: model.sort,
    onSortChange: handleSortChange,
    tableCellContext,
    title: model.endpoint.title,
    titleEndAdornment: props.titleEndAdornment,
    intentFilters: model.intentFilters,
    snapshotId: model.snapshotId,
  });

  const lazyLoadRows = useLazyLoadRows({
    getLoadedRows: () =>
      workaroundExtractModelCurrentState(model.dispatch).data,
    getRowCount: () =>
      workaroundExtractModelCurrentState(model.dispatch).rowCount,
    onLoadChunk: async (fromIndex, toIndex) => {
      await model.dispatch(loadTableRows, {
        start: fromIndex,
        limit: toIndex - fromIndex + 1,
      });
    },
  });

  const apiTableModel = useMemo(
    () => ({
      ...model,
      tableModel,
    }),
    [model, tableModel],
  );

  useReloadOnAddedColumns(apiTableModel);

  const initialFilters = useMemo(
    () => props.initialFilters ?? props.initialFilterPreset?.filter ?? {},
    [props.initialFilters, props.initialFilterPreset],
  );

  useEffectOnChange(() => {
    model.dispatch(reloadTableData);
  }, [snapshotId, globalCtxProps.attributeFilters, props.bindVariables]);

  useEffectOnChange(() => {
    // avoid reload when externally provided filters correspond to current filters
    if (!deepEqual(model.filters, initialFilters)) {
      model.dispatch(reloadTableData);
    }
  }, [initialFilters]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger on snapshot change
  useEffect(() => {
    model.dispatch(loadTableReports);
  }, [snapshotId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(apiTableModel.dispatch): stable
  useEffect(() => {
    apiTableModel.dispatch(applyExternalFilters, { filters: initialFilters });
  }, [initialFilters]);

  return apiTableModel;
};
